import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { useAuth } from '../context/AuthContext';
import { orderService } from '../services/api';
import type { PaymentConfig, ApiError } from '../services/api';
import type { CartItem, ShippingAddress } from '../types';
import { formatCentsUSD } from '../lib/money';
import { computeCartTotals, computeSubtotalCentsUSD } from '../lib/pricing';
import { usePricingConfig } from '../hooks/usePricingConfig';
import { loadSquareSdk } from '../lib/square';
import type { SquareCard } from '../lib/square';
import { ErrorNotice } from '../components/ErrorNotice';
import { OrderingComingSoon } from '../components/OrderingComingSoon';
import { Lock, Truck, User, LogIn, Loader2 } from 'lucide-react';

interface CheckoutPageProps {
  items: CartItem[];
  onOrderComplete: () => void;
}

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY',
  'LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND',
  'OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
];

export function CheckoutPage({ items, onOrderComplete }: CheckoutPageProps) {
  const navigate = useNavigate();
  const { user, token, login, setSession, loading: authLoading } = useAuth();

  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [shipping, setShipping] = useState<ShippingAddress>({
    name: '', line1: '', line2: '', city: '', state: '', zip: '',
  });

  const [wantAccount, setWantAccount] = useState(false);
  const [password, setPassword] = useState('');
  const [subscribeNewsletter, setSubscribeNewsletter] = useState(false);

  const [showLogin, setShowLogin] = useState(false);
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [errorId, setErrorId] = useState<string | undefined>(undefined);

  const [paymentConfig, setPaymentConfig] = useState<PaymentConfig | null>(null);
  const [cardReady, setCardReady] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const cardRef = useRef<SquareCard | null>(null);

  // Pricing always comes from the backend — never hardcoded. null until loaded.
  const pricing = usePricingConfig();

  useEffect(() => {
    let cancelled = false;
    let cardInstance: SquareCard | null = null;

    orderService.getPaymentConfig()
      .then(async (cfg) => {
        if (cancelled) return;
        setPaymentConfig(cfg);
        if (!cfg.enabled || !cfg.applicationId || !cfg.locationId) return;

        try {
          const sdk = await loadSquareSdk(cfg.environment);
          const payments = sdk.payments(cfg.applicationId, cfg.locationId);
          const card = await payments.card();
          await card.attach('#square-card-container');
          if (cancelled) {
            card.destroy().catch(() => {});
            return;
          }
          cardInstance = card;
          cardRef.current = card;
          setCardReady(true);
        } catch (err) {
          if (!cancelled) {
            setPaymentError(err instanceof Error ? err.message : 'Could not initialize the card form');
          }
        }
      })
      .catch(() => {
        // Config endpoint unreachable — fall back to demo mode (no card form).
        if (!cancelled) setPaymentConfig({ provider: 'square', enabled: false, applicationId: '', locationId: '', environment: 'sandbox' });
      });

    return () => {
      cancelled = true;
      if (cardInstance) cardInstance.destroy().catch(() => {});
      cardRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (user) {
      setCustomerName(user.name);
      setCustomerEmail(user.email);
      setShowLogin(false);
      if (user.defaultAddress) {
        setShipping({
          name: user.defaultAddress.name,
          line1: user.defaultAddress.line1,
          line2: user.defaultAddress.line2 || '',
          city: user.defaultAddress.city,
          state: user.defaultAddress.state,
          zip: user.defaultAddress.zip,
        });
      }
    }
  }, [user]);

  // Retail-only "Coming Soon" mode — checkout is closed (and the backend
  // rejects it too, so this can't be bypassed by navigating here directly).
  if (pricing && !pricing.onlineOrderingEnabled) {
    return <OrderingComingSoon />;
  }

  if (items.length === 0) {
    return (
      <div className="py-16">
        <div className="container-max text-center">
          <p className="text-lg text-gray-500 mb-8">Your cart is empty</p>
          <Link to="/products"><Button size="lg">Continue Shopping</Button></Link>
        </div>
      </div>
    );
  }

  // Subtotal needs no pricing rules; shipping/tax/total come from the
  // backend-provided pricing once it loads (so we never show fabricated totals).
  const subtotalCentsUSD = computeSubtotalCentsUSD(items);
  const totals = pricing ? computeCartTotals(items, pricing) : null;

  async function handleLogin() {
    if (!customerEmail) {
      setLoginError('Please enter your email address above first.');
      return;
    }
    setLoginError('');
    setLoggingIn(true);
    try {
      await login(customerEmail, loginPassword);
      setShowLogin(false);
      setLoginPassword('');
    } catch (err: unknown) {
      setLoginError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoggingIn(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setErrorId(undefined);
    setPaymentError('');

    try {
      let paymentToken: string | undefined;

      if (paymentConfig?.enabled) {
        if (!cardRef.current) {
          setPaymentError('The payment form is still loading. Please wait a moment and try again.');
          setSubmitting(false);
          return;
        }
        const tokenResult = await cardRef.current.tokenize();
        if (tokenResult.status !== 'OK' || !tokenResult.token) {
          setPaymentError(tokenResult.errors?.[0]?.message || 'Card could not be processed. Check your details and try again.');
          setSubmitting(false);
          return;
        }
        paymentToken = tokenResult.token;
      }

      const result = await orderService.checkout({
        items: items.map(i => ({ productId: i.product.id, quantity: i.quantity })),
        customerEmail,
        customerName,
        shipping,
        createAccount: wantAccount && !user,
        password: wantAccount && !user ? password : undefined,
        subscribeNewsletter,
        paymentToken,
      }, token ?? undefined);

      if (result.token) {
        const me = { id: '', email: customerEmail, name: customerName };
        setSession(me, result.token);
      }

      // Persist the per-order access token so the success page can fetch the
      // order (and survive a refresh) even for guest checkout.
      const accessToken = result.guestAccessToken;
      try {
        if (accessToken) sessionStorage.setItem(`orderAccess:${result.order.id}`, accessToken);
      } catch { /* sessionStorage may be unavailable; success page degrades gracefully */ }

      await orderService.confirm(result.order.id, accessToken);

      // Read-after-write guard: don't send the customer to the confirmation page
      // until the order is actually readable back from the API. With SQLite this
      // returns immediately; with an eventually-consistent store it absorbs
      // replica lag instead of landing on a page that can't load the order yet.
      const readable = await orderService.waitForOrderReadable(result.order.id, accessToken);
      if (!readable) {
        console.warn(`[checkout] order ${result.order.id} not yet readable after write; the confirmation page will keep retrying.`);
      }

      onOrderComplete();
      navigate(`/success?orderId=${result.order.id}`, { state: { guestAccessToken: accessToken } });
    } catch (err: unknown) {
      const apiErr = err as ApiError;
      setError(apiErr?.message || 'Checkout failed');
      setErrorId(apiErr?.errorId);
    } finally {
      setSubmitting(false);
    }
  }

  const updateShipping = (field: keyof ShippingAddress, value: string) =>
    setShipping(s => ({ ...s, [field]: value }));

  return (
    <div>
      <section className="bg-gray-50 py-8">
        <div className="container-max">
          <h1 className="text-3xl font-bold text-gray-900">Checkout</h1>
        </div>
      </section>

      <section className="py-8">
        <div className="container-max">
          <form onSubmit={handleSubmit} className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              {user && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-800">
                  Signed in as <strong>{user.name}</strong> ({user.email})
                  {user.defaultAddress && '. Shipping address pre-filled from your profile.'}
                </div>
              )}

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="w-5 h-5" /> Contact Information
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                      <input type="text" value={customerName} onChange={e => setCustomerName(e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input type="email" value={customerEmail} onChange={e => setCustomerEmail(e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg" required
                        disabled={!!user} />
                      {!user && !authLoading && customerEmail && (
                        <p className="text-xs text-gray-500 mt-1">
                          This email is used for your order confirmation{showLogin ? ' and to sign in' : ''}{wantAccount ? ' and new account' : ''}.
                        </p>
                      )}
                    </div>
                  </div>

                  {!user && !authLoading && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      {!showLogin ? (
                        <button
                          type="button"
                          onClick={() => setShowLogin(true)}
                          className="flex items-center gap-2 text-sm text-coastal-600 hover:text-coastal-700 font-medium"
                        >
                          <LogIn className="w-4 h-4" />
                          Have an account? Sign in to pre-fill your details
                        </button>
                      ) : (
                        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                          <p className="text-sm font-medium text-gray-700">
                            Sign in with <strong>{customerEmail || 'your email'}</strong>
                          </p>
                          <input
                            type="password"
                            placeholder="Password"
                            value={loginPassword}
                            onChange={e => setLoginPassword(e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg"
                          />
                          {loginError && <p className="text-red-600 text-sm">{loginError}</p>}
                          <div className="flex items-center gap-3">
                            <Button type="button" onClick={handleLogin} size="sm" disabled={loggingIn}>
                              {loggingIn ? (
                                <span className="flex items-center gap-2">
                                  <Loader2 className="w-4 h-4 animate-spin" /> Signing in...
                                </span>
                              ) : 'Sign In'}
                            </Button>
                            <button
                              type="button"
                              onClick={() => { setShowLogin(false); setLoginError(''); setLoginPassword(''); }}
                              className="text-sm text-gray-500 hover:text-gray-700"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Truck className="w-5 h-5" /> Shipping Address
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Recipient Name</label>
                      <input type="text" value={shipping.name} onChange={e => updateShipping('name', e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label>
                      <input type="text" value={shipping.line1} onChange={e => updateShipping('line1', e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2 (optional)</label>
                      <input type="text" value={shipping.line2 || ''} onChange={e => updateShipping('line2', e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg" />
                    </div>
                    <div className="grid sm:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                        <input type="text" value={shipping.city} onChange={e => updateShipping('city', e.target.value)}
                          className="w-full px-3 py-2 border rounded-lg" required />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                        <select value={shipping.state} onChange={e => updateShipping('state', e.target.value)}
                          className="w-full px-3 py-2 border rounded-lg bg-white" required>
                          <option value="">Select</option>
                          {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code</label>
                        <input type="text" value={shipping.zip} onChange={e => updateShipping('zip', e.target.value)}
                          pattern="\d{5}(-\d{4})?" className="w-full px-3 py-2 border rounded-lg" required />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Lock className="w-5 h-5" /> Payment
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {paymentConfig === null ? (
                    <p className="text-sm text-gray-500 flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Loading payment options…
                    </p>
                  ) : paymentConfig.enabled ? (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
                      Payment is processed securely via Square. Your card details are entered directly into
                      Square&apos;s hosted fields and never touch our servers.
                    </div>
                  ) : (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
                      Square payments aren&apos;t configured for this store yet, so checkout runs in{' '}
                      <strong>demo mode</strong> and no card is charged.
                    </div>
                  )}

                  <div
                    id="square-card-container"
                    className={paymentConfig?.enabled ? 'mt-4 min-h-[90px]' : 'hidden'}
                  />

                  {paymentConfig?.enabled && !cardReady && !paymentError && (
                    <p className="text-sm text-gray-500 flex items-center gap-2 mt-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Loading secure card form…
                    </p>
                  )}
                  {paymentError && (
                    <p className="text-red-600 text-sm mt-2" role="alert">{paymentError}</p>
                  )}
                  {paymentConfig?.enabled && (
                    <p className="text-xs text-gray-500 mt-3">
                      {paymentConfig.environment === 'sandbox'
                        ? 'Square Sandbox mode. Test card 4111 1111 1111 1111, any future expiry and CVV.'
                        : 'Secured by Square.'}
                    </p>
                  )}
                </CardContent>
              </Card>

              {!user && (
                <Card>
                  <CardContent className="pt-6">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input type="checkbox" checked={wantAccount} onChange={e => setWantAccount(e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-coastal-600 focus:ring-coastal-500" />
                      <span className="text-sm font-medium text-gray-700">
                        Create an account to save shipping info and track orders
                      </span>
                    </label>
                    {wantAccount && (
                      <div className="mt-4">
                        <p className="text-xs text-gray-500 mb-2">
                          Your account will use <strong>{customerEmail || 'the email above'}</strong> as login.
                        </p>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Choose a Password (min 8 characters)</label>
                        <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                          minLength={8} className="w-full px-3 py-2 border rounded-lg" required={wantAccount} />
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardContent className="pt-6">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={subscribeNewsletter} onChange={e => setSubscribeNewsletter(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-coastal-600 focus:ring-coastal-500" />
                    <span className="text-sm font-medium text-gray-700">
                      Email me about new harvests &amp; new locations
                    </span>
                  </label>
                  {subscribeNewsletter && (
                    <p className="text-xs text-gray-500 mt-2 ml-7">
                      We&apos;ll send updates to <strong>{customerEmail || 'your email'}</strong>. Unsubscribe anytime.
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>

            <div>
              <Card className="sticky top-20">
                <CardHeader>
                  <CardTitle>Order Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 mb-4">
                    {items.map(item => (
                      <div key={item.product.id} className="flex justify-between text-sm">
                        <span className="text-gray-600 truncate mr-2">
                          {item.product.name} x{item.quantity}
                        </span>
                        <span className="font-medium flex-shrink-0">
                          {formatCentsUSD(item.product.priceCentsUSD * item.quantity)}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="border-t pt-3 space-y-2 mb-6">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Subtotal</span>
                      <span>{formatCentsUSD(subtotalCentsUSD)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Shipping</span>
                      <span className={totals && totals.shippingCentsUSD === 0 ? 'text-green-600 font-semibold' : (totals ? '' : 'text-gray-400')}>
                        {totals ? (totals.shippingCentsUSD === 0 ? 'Free' : formatCentsUSD(totals.shippingCentsUSD)) : 'Calculating…'}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Tax</span>
                      <span className={totals ? '' : 'text-gray-400'}>{totals ? formatCentsUSD(totals.taxCentsUSD) : 'Calculating…'}</span>
                    </div>
                    <div className="border-t pt-2 flex justify-between font-bold text-lg">
                      <span>Total</span>
                      <span>{totals ? formatCentsUSD(totals.totalCentsUSD) : '—'}</span>
                    </div>
                  </div>

                  {error && (
                    <ErrorNotice message={error} errorId={errorId} className="mb-3" />
                  )}

                  <Button type="submit" className="w-full" size="lg" disabled={submitting || !totals}>
                    {submitting ? 'Processing...' : totals ? `Place Order (${formatCentsUSD(totals.totalCentsUSD)})` : 'Calculating total…'}
                  </Button>
                  <Link to="/cart">
                    <Button type="button" variant="outline" className="w-full mt-3">Back to Cart</Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
